import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { SearchX, RotateCcw, AlertCircle, Caravan } from 'lucide-react';
import { useSearchStore } from '../store/useSearchStore';
import { useAuthStore, homePathFor } from '../store/useAuthStore';
import SearchFilters from '../components/facility/SearchFilters';
import FacilityCard from '../components/facility/FacilityCard';
import VehicleProfile from '../components/facility/VehicleProfile';
import SkeletonCard from '../components/ui/SkeletonCard';
import { BRAND_NAME } from '../lib/brand';
import { useSeo } from '../lib/seo';
import { vehicleKindMeta } from '../lib/fit';
import { formatFeet } from '../lib/format';

/**
 * Public facility discovery. Deliberately outside the auth guard — a visitor
 * has to be able to see prices and availability before creating an account.
 */
export default function SearchPage() {
  useSeo({
    title: 'Available RV & Boat Storage Spaces',
    description:
      'Browse every RV, trailer and boat storage space on Merritt Island with sizes and '
      + 'monthly prices. Enter your rig’s dimensions to see which ones actually fit.',
    path: '/search',
  });

  const fetchResults = useSearchStore((s) => s.fetchResults);
  const status = useSearchStore((s) => s.status);

  useEffect(() => {
    if (status === 'idle') fetchResults();
  }, [status, fetchResults]);

  return (
    <div className="flex min-h-screen flex-col">
      <PublicHeader />
      <SearchFilters />

      {/* The map pane is removed for now. It was a mock canvas — real
          projection, but a blank grid instead of tiles — which told a visitor
          nothing about where the yard actually is. FacilityMap and
          projection.js are kept so it can come back with real tiles. */}
      <div className="min-h-0 flex-1 bg-slate-50">
        <section aria-label="Search results" className="mx-auto max-w-3xl">
          <ResultsList />
        </section>
      </div>
    </div>
  );
}

function PublicHeader() {
  const authStatus = useAuthStore((s) => s.status);
  const user = useAuthStore((s) => s.user);

  return (
    <header className="z-30 shrink-0 border-b border-slate-200 bg-white">
      <div className="flex items-center gap-4 px-4 py-3 sm:px-6">
        <Link to="/" state={{ landing: true }} aria-label={`${BRAND_NAME} home`}
              className="flex shrink-0 items-center gap-2">
          <span className="grid h-9 w-9 place-items-center rounded-lg bg-brand-600 text-white">
            <Caravan className="h-5 w-5" aria-hidden="true" />
          </span>
          <span className="hidden text-lg font-bold tracking-tight text-slate-900 sm:block">{BRAND_NAME}</span>
        </Link>

        <p className="hidden min-w-0 flex-1 truncate text-sm text-slate-500 md:block">
          RV, boat, and vehicle storage — monthly, no long-term commitment.
        </p>

        <div className="ml-auto flex items-center gap-2">
          <VehicleProfile />
          {authStatus === 'signed_in' && user ? (
            <Link to={homePathFor(user.role)} className="btn-ghost py-2 text-sm">My account</Link>
          ) : (
            <Link to="/login" className="btn-ghost py-2 text-sm">Sign in</Link>
          )}
        </div>
      </div>
    </header>
  );
}

function ResultsList() {
  const results = useSearchStore((s) => s.results);
  const status = useSearchStore((s) => s.status);
  const error = useSearchStore((s) => s.error);
  const total = useSearchStore((s) => s.pagination.total);
  const hasMore = useSearchStore((s) => s.pagination.hasMore);
  const loadMore = useSearchStore((s) => s.loadMore);
  const resetFilters = useSearchStore((s) => s.resetFilters);
  const setFilter = useSearchStore((s) => s.setFilter);
  const includeUnavailable = useSearchStore((s) => s.filters.includeUnavailable);
  const selectedId = useSearchStore((s) => s.selectedFacilityId);
  const vehicle = useSearchStore((s) => s.vehicle);
  const listRef = useRef(null);

  // Every search is filtered by fit, so every price shown is the cheapest
  // space that fits *this* rig. Naming the rig is what stops "$125 and up"
  // reading as a contradiction of the $80 spaces the yard also has.
  const vehicleLabel = `${vehicleKindMeta(vehicle.kind).label} at ${formatFeet(vehicle.length)}`;

  /** Clicking a pin scrolls its card into view — the other half of the sync. */
  useEffect(() => {
    if (!selectedId || !listRef.current) return;
    listRef.current.querySelector(`[data-facility-id="${selectedId}"]`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [selectedId]);

  const initialLoad = status === 'loading' && results.length === 0;

  return (
    <div className="flex flex-col">
      <div className="shrink-0 px-4 py-3">
        <h1 className="text-base font-bold text-slate-900">
          {initialLoad ? 'Searching…' : `${total} facilit${total === 1 ? 'y' : 'ies'} in this area`}
        </h1>
        <p className="text-xs text-slate-500">
          Prices are the cheapest space that fits a {vehicleLabel}, per month
          before tax and one-time fees. Smaller spaces may cost less but will not fit.
        </p>
        {includeUnavailable && (
          <p className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-slate-600">
            Including facilities with no space free.
            <button type="button" onClick={() => setFilter('includeUnavailable', false)}
                    className="font-bold text-brand-700 hover:underline">
              Show only what is available
            </button>
          </p>
        )}
      </div>

      <div ref={listRef} className="min-h-0 flex-1 space-y-3 px-4 pb-6">
        {initialLoad && Array.from({ length: 4 }, (_, i) => <SkeletonCard key={i} />)}

        {status === 'error' && (
          <EmptyState Icon={AlertCircle} title="We couldn't load results" body={error}
                      actionLabel="Try again" onAction={() => useSearchStore.getState().fetchResults()} />
        )}

        {status === 'success' && results.length === 0 && (
          includeUnavailable ? (
            <EmptyState Icon={SearchX} title="Nothing here at all"
                        body="Not even a facility with no space free. Try widening the search area."
                        actionLabel="Clear filters" onAction={resetFilters} />
          ) : (
            // Everything full is a different problem from filters being too
            // tight, and it has a different answer: see the yard anyway, get
            // on a list. Offering only "clear filters" sends someone to widen
            // filters that were never the reason.
            <EmptyState Icon={SearchX} title="No spaces available that fit"
                        body="Every space that fits your rig is taken right now. You can still see the facility and what it has."
                        actionLabel="Show facilities with no space free"
                        onAction={() => setFilter('includeUnavailable', true)} />
          )
        )}

        {results.map((facility) => (
          <div key={facility.public_id} data-facility-id={facility.public_id}>
            <FacilityCard facility={facility} />
          </div>
        ))}

        {hasMore && (
          <button type="button" onClick={loadMore} disabled={status === 'loading'} className="btn-ghost w-full">
            {status === 'loading' ? 'Loading…' : 'Show more facilities'}
          </button>
        )}
      </div>
    </div>
  );
}

function EmptyState({ Icon, title, body, actionLabel, onAction }) {
  return (
    <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center">
      <Icon className="mx-auto h-8 w-8 text-brand-300" aria-hidden="true" />
      <p className="mt-3 text-sm font-bold text-slate-900">{title}</p>
      <p className="mx-auto mt-1 max-w-xs text-xs text-slate-500">{body}</p>
      <button type="button" onClick={onAction} className="btn-ghost mx-auto mt-4 py-2">
        <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" /> {actionLabel}
      </button>
    </div>
  );
}
